<template>
    <tailwind-layout title="Maintenance: Add Property">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Maintenance: Add Property</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('maintenance.index')">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>
        </template>
        <div class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <form @submit.prevent="store" class="space-y-7 divide-y divide-gray-200">
                <div class="space-y-8 divide-y divide-gray-200">
                    <div>
                        <div>
                            <h3 class="text-lg leading-6 font-medium text-gray-900">
                                Maintenance Property Details
                            </h3>
                        </div>
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div class="sm:col-span-2">
                                <label for="existing_property" class="block text-sm font-medium text-gray-700">
                                    Use Existing Properties
                                </label>
                                <div class="mt-1">
                                    <select v-model="form.existing_property" @change="useExistingProperty($event)" id="existing_property" data-demo-target="use-existing-address" class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <option value="">Select Any Property</option>
                                        <option v-for="(property, index) in existing_properties" :value="property.id">{{ property.full_address }}</option>
                                    </select>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="owner_name" v-bind:class="(form.errors.owner_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Owner/Landlord Name
                                </label>
                                <div class="mt-1">
                                    <input id="owner_name" v-model="form.owner_name" type="text" data-demo-target="owner_name" v-bind:class="(form.errors.owner_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                    <p v-text="form.errors.owner_name" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-lookup" class="block text-sm font-medium text-gray-700">
                                    Address Lookup
                                </label>
                                <div class="mt-1">
                                    <div class="mt-1 relative rounded-md">
                                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <font-awesome-icon :icon="faMapMarkerAlt" class="text-gray-400" />
                                        </div>
                                        <GMapAutocomplete
                                            id="address_lookup"
                                            ref="autocompleteRef"
                                            placeholder="Start typing address..."
                                            @place_changed="initAutocomplete"
                                            :options="{
                                                componentRestrictions: { country: ['US', 'VI', 'AS', 'GU', 'PR', 'MP'] }
                                            }"
                                            v-bind:class="'py-0.5 h-10 px-1 mt-1 shadow-sm h-8 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md pl-10'"
                                            data-demo-target="address_lookup"
                                        >
                                        </GMapAutocomplete>
                                    </div>
                                    <p class="text-xs text-gray-500">Please include postal code for accuracy.</p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-1" v-bind:class="(form.errors.address_1)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 1 <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="address_1" v-model="form.address_1" readonly :error="form.errors.address_1" type="text" v-bind:class="(form.errors.address_1)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md bg-gray-100':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100  bg-gray-100'">
                                    <p v-text="form.errors.address_1" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-2" v-bind:class="(form.errors.address_2)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 2
                                </label>
                                <div class="mt-1">
                                    <input id="address-2" v-model="form.address_2" type="text" v-bind:class="(form.errors.address_2)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="city" v-bind:class="(form.errors.city)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    City <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="city" v-model="form.city" readonly type="text" v-bind:class="(form.errors.city)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md  bg-gray-100'">
                                    <p v-text="form.errors.city" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="state" v-bind:class="(form.errors.state)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    State <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input v-model="form.state" type="hidden">
                                    <select id="state" disabled v-bind:value="form.state" v-model="form.state" :error="form.errors.state" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100'">
                                        <option v-for="(state, index) in states" :value="index">{{ state }}</option>
                                    </select>
                                    <p v-text="form.errors.state" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="zip_code" v-bind:class="(form.errors.zip_code)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Zip Code <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="zip_code" v-model="form.zip_code" readonly type="text" v-bind:class="(form.errors.zip_code)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100'">
                                    <p v-text="form.errors.zip_code" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div>
                    </div>
            </div>

                <div>
                    <div class="sm:col-span-6">
                        <h3 class="text-lg leading-6 font-medium text-gray-900 mt-2">
                            Tenant Maintenance Instructions
                        </h3>
                        <p class="text-xs leading-6 font-medium text-gray-500">These are the instructions your tenant will see on their dashboard once you invite them to the portal.</p>
                    </div>

                    <div>
                        <div class="mt-7 grid grid-cols-1 gap-y-12 gap-x-4 sm:grid-cols-6">
                            <div class="sm:col-span-2">
                                <label for="tenant_email"
                                    v-bind:class="(form.errors.tenant_email)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Tenant Email
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 flex items-center pointer-events-none"
                                        v-bind:class="(form.errors.tenant_email)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faEnvelope" class="text-gray-400" />
                                    </div>
                                    <input id="tenant_email" v-model="form.tenant_email" type="email" data-demo-target="invite-tenant"
                                        v-bind:class="(form.errors.tenant_email)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'"
                                    >
                                    <p v-text="form.errors.tenant_email" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="tenant_message" v-bind:class="(form.errors.tenant_message)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                   Email sent to Tenant
                                    <tool-tip
                                        :title="'Email sent to Tenant'"
                                        :description="'This is the message you want your tenant to receive once they put in a request. The below message is the default message.'"
                                    ></tool-tip>
                                </label>
                                <div class="mt-1">
                                    <textarea
                                        id="tenant_message"
                                        rows="4" v-model="form.tenant_message"
                                        :error="form.errors.tenant_message"
                                        placeholder="Custom message received by tenant.."
                                        v-bind:class="(form.errors.tenant_message)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >

                                    </textarea>
                                </div>
                            </div>

                            <!-- <div class="sm:col-span-2">
                                <label class="text-sm font-medium text-gray-700">Request Types</label>
                                <div class="relative mt-0">
                                    <button type="button" @click="toggleEmergencyDropdown" ref="emergencyDropdownButton"
                                        class="w-full flex justify-between items-center border rounded-md bg-white shadow-sm px-3 py-2 text-sm text-gray-700 focus:ring-indigo-500 focus:border-indigo-500">
                                        <span v-if="form.selectedEmergencies.length && form.selectedEmergencies.some(id => getEmergencyName(id) !== 'Unknown')">
                                            {{ form.selectedEmergencies.map(id => getEmergencyName(id)).filter(name => name !== 'Unknown').join(', ') }}
                                        </span>
                                        <span v-else class="text-gray-400">Select Request Types</span>
                                        <svg class="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </button>

                                    <div v-if="dropdownOpenEmergencies && maintenanceEmergencies.length" ref="emergencyDropdownMenu"
                                        class="absolute left-0 mt-2 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-hidden z-50"
                                        style="max-height: 200px; overflow-y: auto;">

                                        <div v-for="emergency in maintenanceEmergencies" :key="emergency.id"
                                            class="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                                            <input type="checkbox" v-model="form.selectedEmergencies" :value="emergency.id"
                                                class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                            <label class="ml-2 text-sm text-gray-900">{{ emergency.name }} <span v-if="emergency.description" class="text-gray-400">{{ emergency.description }}</span></label>
                                        </div>
                                    </div>

                                </div>
                            </div> -->

                            <div class="sm:col-span-2">
                                <label for="repair_amount" v-bind:class="(form.errors.repair_amount)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Tenant is responsible for any repairs up to ($)
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div class="absolute left-0 pl-2 flex items-center pointer-events-none" v-bind:class="(form.errors.repair_amount)?'top-3':'inset-y-0'">

                                    </div>
                                    <input id="repair_amount" v-model="form.repair_amount" type="number" step="0.01" v-bind:class="(form.errors.repair_amount)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                    <p v-text="form.errors.repair_amount" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="specific_instructions" v-bind:class="(form.errors.specific_instructions)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                    Specific Instructions
                                    <tool-tip
                                        :title="'Specific Instructions'"
                                        :description="'This is where you tell your tenant how you want them to handle the maintenance request. For example, Call Home Warranty Company for plumbing or electrical issues.'"
                                    ></tool-tip>
                                </label>
                                <div class="mt-1">
                                    <textarea
                                        id="specific_instructions"
                                        rows="2" v-model="form.specific_instructions"
                                        :error="form.errors.specific_instructions"
                                        placeholder="Any other specific instructions for the tenant"
                                        v-bind:class="(form.errors.specific_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:ring-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'"
                                    >
                                    </textarea>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="guidelines" v-bind:class="(form.errors.guidelines)?'block text-sm inline-flex font-medium text-red-500':'block text-sm inline-flex font-medium text-gray-700'">
                                    Emergency Guidlines
                                    <tool-tip
                                        :title="'Emergency Guidlines'"
                                        :description="'Please call landlord directly if there is any water emergency such as a leaking roof, flooding basement, etc.'"
                                    ></tool-tip>
                                </label>
                                <div class="mt-1">
                                    <textarea
                                        id="guidelines"
                                        rows="2" v-model="form.guidelines"
                                        :error="form.errors.guidelines"
                                        placeholder="Any guidelines for the tenant"
                                        v-bind:class="(form.errors.guidelines)?'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-300 rounded-md'"
                                        data-demo-target="emergency-contact"
                                    >
                                    </textarea>
                                </div>
                            </div>

                            <div class="sm:col-span-6 border-b pb-0 mb-0">
                                <h3 class="text-lg font-medium text-gray-900">Home Warranty Company</h3>
                            </div>
                            <div class="sm:col-span-6 mt-[-20px]">
                                <label class="text-sm font-medium text-gray-700">Tenant may contact directly?</label>
                                <div class="mt-1 flex space-x-4">
                                    <label class="flex items-center space-x-2 cursor-pointer">
                                        <input type="radio" v-model="form.tenant_can_contact_directly" :value="true"
                                            class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                        <span class="text-sm text-gray-700">Yes</span>
                                    </label>
                                    <label class="flex items-center space-x-2 cursor-pointer">
                                        <input type="radio" v-model="form.tenant_can_contact_directly" :value="false"
                                            class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500">
                                        <span class="text-sm text-gray-700">No</span>
                                    </label>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.warranty_company_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Company Name
                                </label>
                                <input type="text" v-model="form.warranty_company_name"
                                    v-bind:class="(form.errors.warranty_company_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.warranty_company_name" class="text-red-500 text-sm"></p>
                            </div>
                            <div class="sm:col-span-2">
                                <div>
                                    <label v-bind:class="(form.errors.company_phone)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">Company Phone Number</label>
                                    <input type="text" v-model="form.company_phone"
                                    v-bind:class="(form.errors.company_phone)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                        <p v-text="form.errors.company_phone" class="text-red-500 text-sm"></p>
                                        <p class="text-gray-500 text-xs">The phone number must be 10 digits. Please remove any dashes or symbols.</p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.company_website)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Website
                                </label>
                                <input type="text" v-model="form.company_website"
                                    placeholder="https://www.example.com"
                                    v-bind:class="(form.errors.company_website)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.company_website" class="text-red-500 text-sm"></p>
                                <p class="text-gray-500 text-xs">
                                    Please include the https:// before the URL.
                                </p>
                            </div>
                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.contract_number)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Contract Number
                                </label>
                                <input type="text" v-model="form.contract_number"
                                    v-bind:class="(form.errors.contract_number)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'">
                                <p v-text="form.errors.contract_number" class="text-red-500 text-sm"></p>
                            </div>
                            <div class="sm:col-span-2">
                                <label v-bind:class="(form.errors.warranty_additional_instructions)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Additional Instructions
                                </label>
                                <textarea v-model="form.warranty_additional_instructions" rows="2"
                                    v-bind:class="(form.errors.warranty_additional_instructions)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-2':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-2'"
                                    placeholder="Any specific instructions for the tenant regarding the warranty company"></textarea>
                                <p v-text="form.errors.warranty_additional_instructions" class="text-red-500 text-sm"></p>
                            </div>
                        </div>

                    </div>
                </div>
                <div class="pt-5">
                    <!-- Terms & Conditions Section -->
                    <div v-if="subscription_data.has_access && !subscription_data.is_terms_accepted" class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                        <h3 class="text-lg font-medium text-blue-800 mb-4">Terms and Conditions</h3>
                        <p class="text-sm text-blue-700 mb-4">You must accept the Terms and Conditions to use the maintenance module.</p>
                        
                        <div class="mb-4">
                            <label class="flex items-start">
                                <input 
                                    type="checkbox" 
                                    v-model="form.maintenance_terms_accepted" 
                                    class="mt-1 h-4 w-4 text-rent-risk-blue-400 focus:ring-rent-risk-blue-400 border-gray-300 rounded"
                                />
                                <span class="ml-2 text-sm text-gray-700">
                                    I agree to the <a target="_blank" :href="appUrl('/terms-of-service')" class="text-rent-risk-blue-400 underline">Terms and Conditions</a> for the maintenance module
                                </span>
                            </label>
                            <p v-if="form.errors.maintenance_terms_accepted" class="text-red-500 text-sm mt-1">{{ form.errors.maintenance_terms_accepted }}</p>
                            <p v-if="termsErrorMessage" class="text-red-500 text-sm mt-1">{{ termsErrorMessage }}</p>
                        </div>
                        
                        <div class="flex justify-end">
                            <button 
                                type="button" 
                                @click="acceptMaintenanceTerms"
                                :disabled="!form.maintenance_terms_accepted"
                                class="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Accept Terms
                            </button>
                        </div>
                    </div>

                    <!-- 86bcd9hg3 (decision 18): maintenance is part of RiskGuard Pro, not sold on its
                         own. The button opens the Pro pop-up and buys right here. -->
                    <div v-if="!subscription_data.has_access" class="mb-6 rounded-lg border border-[#e0cb85] bg-[#fbf6e3] p-6" data-test="maintenance-pro-card">
                        <h3 class="text-base font-semibold text-gray-900">Maintenance requests are part of RiskGuard Pro</h3>
                        <p class="mt-1 text-sm text-gray-700">Upgrade your account to Pro to accept maintenance requests on every property, along with rent payments, leases and more.</p>
                        <ProUpgradeButton
                            :stripe-data="stripe_data"
                            :has-payment-method="Boolean(subscription_data.payment_method)"
                            :user-payment-method="subscription_data.payment_method"
                        />
                    </div>

                    <!-- Success Message -->
                    <div v-if="subscription_data.has_access && subscription_data.is_terms_accepted" class="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <font-awesome-icon :icon="faCheckCircle" class="text-green-400" />
                            </div>
                            <div class="ml-3">
                                <p class="text-sm font-medium text-green-800">
                                    You have accepted the terms and have an active subscription. You can create maintenance properties.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end">
                        <inertia-link :href="route('maintenance.index')" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400">Cancel</inertia-link>
                        <form-button 
                            :form="form" 
                            type="button" 
                            data-demo-target="save-button" 
                            @click="showFraudPreventionModal" 
                            :disabled="!subscription_data.has_access || !subscription_data.is_terms_accepted"
                            class="ml-3"
                        >
                            Save
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    </tailwind-layout>
    <div v-if="fraudPreventionModal">
        <TransitionRoot as="template" :show="fraudPreventionModal">
            <Dialog as="div" class="relative z-50" @close="fraudPreventionModal = false">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                        <font-awesome-icon :icon="faExclamation" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">As a fraud prevention measure you must be authenticated by Experian before accepting applications.</DialogTitle>
                                        <div class="mt-2">
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex">
                                    <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="fraudPreventionModal = false">Cancel</button>
                                    <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="showCAComplianceModal">Proceed</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>

      <!-- Add Option Modal (Used for Add & Edit) -->
      <div v-if="showOptionModal">
            <TransitionRoot as="template" :show="showOptionModal">
                <Dialog as="div" class="relative z-10" @close="closeOptionModal">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-6 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                    <div class="text-center sm:text-left">
                                        <DialogTitle as="h3" class="text-lg font-semibold text-gray-900">
                                            Add New Option
                                        </DialogTitle>
                                        <div class="mt-2">
                                            <!-- eslint-disable-next-line vue/no-undef-properties -->
                                            <label for="name" :class="errors.name ? 'block text-sm font-medium text-red-500' : 'block text-sm font-medium text-gray-700'">
                                                Name
                                            </label>
                                            <input id="name" type="text" v-model="optionForm.name"
                                                    :class="errors.name ? 'mt-1 w-full border-red-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm' : 'mt-1 w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'">
                                                <p v-if="errors.name" class="text-red-500 text-sm">{{ errors.name }}</p>
                                        </div>
                                    </div>

                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="w-1/2 mr-1 justify-center rounded-md bg-gray-50 border-gray-400 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus:outline-none" @click="closeOptionModal">
                                            Cancel
                                        </button>
                                        <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                            @click="saveOption">
                                            {{ isEditing ? 'Save' : 'Add Option' }}
                                        </button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

    <TransitionRoot as="template" :show="cAComplianceModal">
        <Dialog as="div" class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <h1 class="font-semibold text-2xl leading-tight">For California Landlords and Property Managers</h1>
                            <h2 class="mt-4">California landlords and property managers are in compliance with the California Investigative Consumer Reporting Agencies Act (ICRAA).</h2>
                            <div class="flex gap-2 mt-4">
                                <input id="ca_icraa_compliance" type="checkbox" v-model="form.ca_icraa_compliance" class="focus:ring-rent-risk-blue-400 h-4 w-4 text-rent-risk-blue-400 border-gray-300 rounded mt-2" />
                                <label for="ca_icraa_compliance"><h2>I have read and agree to the California Consumer Credit Reporting Agencies Act Disclosure and Authorization.</h2></label>
                            </div>
                            <p v-text="form.errors.ca_icraa_compliance" class="text-red-500 text-sm"></p>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                            <button type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-indigo-300 shadow-sm px-4 py-2 bg-rent-risk-blue-400 text-white font-medium hover:bg-rent-risk-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" @click="store">Save</button>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>

    <!-- Add Credit Card Modal -->

    <DemoWidget
        :page-identifier="'maintenance.create'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import SwitchGroup from "@/Shared/SwitchGroup.vue";
import RadioGroup from "@/Shared/RadioGroup.vue";
import BannedCitiesAndStates from "@/Shared/BannedCitiesAndStates.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import axios from "axios";
import SmallCards from "@/Shared/SmallCards.vue";
import {Dialog, DialogOverlay, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {usePage} from "@inertiajs/vue3";
import Label from "@/Jetstream/Label.vue";
import ToolTip from "@/Shared/ToolTip.vue";
import { getDemoSteps } from '@/Config/demoSteps.js'
import DemoWidget from '@/Components/DemoWidget.vue';
import ProUpgradeButton from '@/Components/ProUpgradeButton.vue';
import {
    faCheckCircle,
    faChevronLeft,
    faCreditCard,
    faEnvelope,
    faExclamation,
    faMapMarkerAlt
} from '@fortawesome/pro-solid-svg-icons';
export default {
    props: {
        states: Object,
        maintenanceEmergencies: Array,
        vendors: Array,
        existing_properties: Object,
        subscription_data: Object,
        stripe_data: Object,
    },
    components: {
        ToolTip,
        Label,
        DialogOverlay,
        DialogTitle,
        DialogPanel,
        TransitionChild,
        Dialog,
        TransitionRoot,
        SmallCards,
        BannedCitiesAndStates,
        FontAwesomeIcon,
        SwitchGroup,
        TailwindLayout,
        FormButton,
        SelectInput,
        RadioGroup,
        DemoWidget,
        ProUpgradeButton,
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                existing_property: '',
                owner_name: null,
                address_1: null,
                address_2: null,
                city: null,
                state: null,
                county: null,
                zip_code: null,
                rent: null,
                status: 1,
                type: 3,
                tenant_email: null,
                tenant_message: "Your request has been received and will be responded to in 24 hours. If this is a maintenance emergency, please call xxx-xxx-xxxx. If you are in danger, please call 911.",
                repair_amount: null,
                specific_instructions: null,
                guidelines:null,
                tenant_can_contact_directly: null,
                warranty_company_name: null,
                company_phone: null,
                company_website: null,
                contract_number: null,
                warranty_additional_instructions: null,
                // Subscription related fields
                terms_accepted: false,
                maintenance_terms_accepted: false,
                selected_plan: 'monthly'
            }),
            termsErrorMessage: null,
            // Written by the dropdown, option modal and address lookup below; declared so they are reactive.
            dropdownOpen: false,
            isEditing: false,
            address_lookup: null,
            twoStepAddress: null,
            fraudPreventionModal: false,
            cAComplianceModal: false,
            dropdownOpenEmergencies: false,
            dropdownOpenVendors: false,
            showOptionModal: false,
            optionForm: {
                id: null,
                name: '',
            },
            errors: { name: null },
            customEmergency: "",
            customEmergencies: [],
            faChevronLeft,
            faCheckCircle,
            faCreditCard,
            faEnvelope,
            faExclamation,
            faMapMarkerAlt,
            demoSteps: getDemoSteps('maintenance.create'),
        }
    },
    mounted() {
        this.form.reset();
        document.addEventListener("click", this.closeDropdownOnClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.closeDropdownOnClickOutside);
    },
    watch: {
        'form.is_agent_fee'(newValue, oldValue) {
            this.form.is_applicant_fee = newValue === 1 ? 0 : 1;
        },
        'form.is_applicant_fee'(newValue, oldValue) {
            this.form.is_agent_fee = newValue === 1 ? 0 : 1;
        },

    },
    methods: {
        useExistingProperty(event) {
            const property_id = Number(event.target.value); // Convert to correct type
            if (!property_id) {
                this.form.reset();
            } else {
                const property = this.existing_properties.find(
                    property => property.id === property_id
                );

                if (property) {
                    this.form.owner_name = property.owner_name;
                    this.form.address_1 = property.address_1;
                    this.form.address_2 = property.address_2;
                    this.form.city = property.city;
                    this.form.state = property.state;
                    this.form.county = property.county;
                    this.form.zip_code = property.zip_code;
                    this.form.rent = property.rent;
                }
            }
        },

        closeDropdownOnClickOutside(event) {
            if (
                this.$refs.emergencyDropdownMenu && !this.$refs.emergencyDropdownMenu.contains(event.target) &&
                this.$refs.emergencyDropdownButton && !this.$refs.emergencyDropdownButton.contains(event.target)
            ) {
                this.dropdownOpenEmergencies = false;
            }

            if (
                this.$refs.vendorDropdownMenu && !this.$refs.vendorDropdownMenu.contains(event.target) &&
                this.$refs.vendorDropdownButton && !this.$refs.vendorDropdownButton.contains(event.target)
            ) {
                this.dropdownOpenVendors = false;
            }
        },
        closeDropdownAfterSelection() {
            setTimeout(() => {
                // eslint-disable-next-line vue/no-undef-properties
                this.dropdownOpen = false;
            }, 200); // Delay to prevent instant closing on click
        },
        myToggleFunction: function(event, form){
            form.status = form.status === false;
        },
        acceptRentToggleFunction: function(event, form){
            form.type = form.type === false;
        },
        showFraudPreventionModal() {
            if (this.form.status) {
                if(this.form.address_1 && this.form.city && this.form.state && this.form.zip_code && this.form.availability_date) {
                    // checking if user is not authenticated with experian, then showing the fraud prevention popup
                    if(!usePage().props.auth.user.connect_api_user_token || !usePage().props.auth.user.connect_api_auth_session) {
                        this.fraudPreventionModal = true;
                    }
                    else {
                        this.showCAComplianceModal();
                    }
                } else {
                    this.store();
                }
            } else {
                this.store();
            }
        },
        showCAComplianceModal() {
            this.fraudPreventionModal = false;
            if (this.form.state === 'CA') {
                this.cAComplianceModal = true;
            } else {
                this.store();
            }

        },
        fetchData(url = null) {
            this.$inertia.visit(url || this.route('maintenance.emergency-options.index'), {
                method: 'get',
                preserveState: false,
                replace: true,
                data: this.form,
            });
        },
        saveOption() {
            // Add Request Option
            this.$inertia.post(this.route('emergency-options.store'), this.optionForm, {
                onSuccess: () => {
                    this.showOptionModal = false;
                    this.errors = {};
                    },
                    onError: (errors) => {
                        this.errors = errors;
                    }
                });
        },
        openAddModal() {
            // eslint-disable-next-line vue/no-undef-properties
            this.isEditing = false;
            this.optionForm = { id: null, name: '' };
            this.errors = {};
            this.showOptionModal = true;
        },
        closeOptionModal() {
            this.showOptionModal = false;
            this.errors = {};
        },
        store() {
            this.fraudPreventionModal = false;
            if(this.cAComplianceModal && !this.form.ca_icraa_compliance) {
                this.form.errors.ca_icraa_compliance = 'The California compliance must be acknowledged when the property is located in California.';
                return false;
            }
            this.cAComplianceModal = false;
            //this.form.post(this.route('maintenance.store'))
            this.form.post(this.route('maintenance.store'), {
                selectedEmergencies: this.form.selectedEmergencies,
                selectedVendors: this.form.selectedVendors
            });
        },
        initAutocomplete(place){
            // RAC-9990 — see Pages/V2/Properties/AddProperty.vue for the full story.
            // Surface inline error via form.errors.address_1 (existing template binding).
            if (!place?.address_components) {
                this.form.errors.address_1 = "We couldn't read a full address from that result. Please pick a more specific result from the dropdown — make sure it's a street address, not a business or area.";
                return;
            }
            this.form.errors.address_1 = null;

            let street = null;
            let route = null;
            this.form.city = null;

            place.address_components.forEach(component => {
                const componentType = component.types[0];
                switch (componentType) {
                    case "street_number": {
                        street = `${component.long_name}`;
                        break;
                    }
                    case "route": {
                        route = component.short_name;
                        break;
                    }
                    case "postal_code": {
                        this.form.zip_code = `${component.long_name}`;
                        break;
                    }
                    case "locality":
                        this.form.city = component.long_name;
                        break;
                    case "administrative_area_level_3":
                        if (this.form.city === null) {
                            this.form.city = component.long_name;
                        }
                        break;
                    case "administrative_area_level_2":
                        this.form.county = component.long_name;
                        break;
                    case "administrative_area_level_1": {
                        this.form.state = `${component.short_name}`;
                        break;
                    }
                    case "country": {
                        if(['VI', 'AS', 'GU', 'PR', 'MP'].includes(component.short_name)) {
                            this.form.state = `${component.short_name}`;
                        }
                        break;
                    }
                }
            });
            if(street === null || route === null) {
                this.form.address_1 = null;
                this.form.errors.address_1 = 'Address 1 not found. Enter the full address in Google search.';
            } else {
                this.form.address_1 = `${street} ${route}`
                this.form.errors.address_1 = null;
                // eslint-disable-next-line vue/no-undef-properties
                this.address_lookup = null;
                this.isPropertyTwoStep();
            }
            this.$refs.autocompleteRef.$refs.input.value = null;
        },

        isPropertyTwoStep(){
            const { county, city, state } = this.form;
            axios.get(route('properties.is-two-step'), {
                params: {
                    county: county,
                    city: city,
                    state: state,
                }
            }).then(response => {
                // eslint-disable-next-line vue/no-undef-properties
                this.twoStepAddress = response.data;
            });
        },
        
        acceptMaintenanceTerms() {
            if (!this.form.maintenance_terms_accepted) return;

            // Store current scroll position
            const currentScrollY = window.scrollY;
            this.termsErrorMessage = null;

            // axios instead of $inertia.post: Inertia v2's router.post returns void,
            // so network failures (ERR_NETWORK, CORS, timeout) bubble to onunhandledrejection
            // instead of onError. Same compliance / audit-trail concern as the subscription
            // ToS popup (RAC-9963) — silent failure leaves no server-side acceptance record.
            axios.post(route('maintenance.accept-terms'), {})
                .then(() => {
                    // Update the local subscription data
                    this.subscription_data.is_terms_accepted = true;
                    this.termsErrorMessage = null;

                    // Restore scroll position and then scroll to subscription section
                    this.$nextTick(() => {
                        window.scrollTo(0, currentScrollY);

                        setTimeout(() => {
                            const subscriptionSection = document.querySelector('.bg-yellow-50, .bg-green-50');
                            if (subscriptionSection) {
                                subscriptionSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 100);
                    });
                })
                .catch((error) => {
                    if (error.response && error.response.data && error.response.data.errors) {
                        this.form.errors = error.response.data.errors;
                    } else if (error.request) {
                        this.termsErrorMessage = 'Network error — please check your connection and tap Accept again.';
                    } else {
                        this.termsErrorMessage = 'Failed to accept terms. Please try again.';
                    }
                });
        }
    }
}
</script>
